import {
    Form,
    FormType,
    ReactiveFormStableValueChange,
} from '@fitogest/reactive-form-builder/interfaces';
import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BaseForm } from 'src/app/view/base-form';
import { list } from '@fitogest/reactive-form-builder/utils';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from 'src/app/service/events/dashboard.service';
import { UsuariosApiService } from 'src/app/service/api/usuarios-api.service';
import paises from 'src/app/common/country';
import { StorageManager } from 'src/app/common/storage-manager.class';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';

import { UserModel } from 'src/app/models/usuarios/user.model';
import { UsuariosFormDefinition, UsuariosFormDefinitionContext } from './form-control/form-definition';

@Component({
    selector: 'app-usuarios-form',
    standalone: false,
    templateUrl: './usuarios-form.component.html',
    styleUrls: ['./usuarios-form.component.scss']
})
export class UsuariosFormComponent extends BaseForm<UserModel> implements OnInit {
    public appName = environment.appName;
    public applicationType = environment.features.applicationType;
    public clienteName: string = environment.features.clienteName;
    public showPerfilUnidades = environment.features.showPerfilUnidades;
    public lista_paises = list<string | null>(['...', null]);

    public override model: UserModel = {} as UserModel;
    public reactiveForm!: { formType: FormType; model: Partial<UserModel>; formFields: Form<UserModel> };
    public reactiveFormRecordId = '';
    public formGetRequest!: RequestHandler<UserModel[]>;
    public nifErrors = '';
    public passErrors = '';
    public showPassErrors = false;
    public isShowingPassword = false;

    public canRegister = environment.features.canRegister;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public usuariosApi: UsuariosApiService,
        public override router: Router
    ) {
        super(
            route,
            router,
            dashboard,
            usuariosApi.usuariosPUT,
            usuariosApi.usuariosPOST,
            'usuarios',
            'Actualizar Usuario',
            'Crear Usuario'
        );

        this.formGetRequest = this.resolveFormGetRequest();
    }

    async ngOnInit() {
        if (this.appName === 'manezylozano') {
            await this.fillCountry();
        }

        this.model = this.initializeHostModel({ ...this.model, ...this.getModel() });
        this.reactiveFormRecordId = this.resolveReactiveFormRecordId(this.model);
        this.prefetchExistingUsersForCreation();
        this.createReactiveForm();
        this.softInit(this.getType());
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<UserModel>): void {
        this.model = this.updateHostModel({ ...this.model, ...event.value } as UserModel);
        if (this.reactiveForm) {
            this.reactiveForm.model = this.model;
        }
    }

    public validateNif(): boolean {
        if (this.getType() === FormType.CREATION) {
            const nifSearch = ((this.model || {}).nif || '').toLowerCase();

            if ((nifSearch || '').length === 0) {
                return true;
            }

            const existeNif = false;

            if (existeNif) {
                this.nifErrors = 'El usuario ya existe';
            } else {
                this.nifErrors = '';
            }

            return !existeNif;
        }

        return true;
    }

    public validatePassword(): boolean {
        this.showPassErrors = false;
        this.passErrors = '';

        if (this.model.password && (this.model.password.trim().length > 0)) {
            if (!this.model.password_repeat) {
                this.passErrors += 'Por favor, repita la contraseña.\n';
                return false;
            }
            if (this.model.password !== this.model.password_repeat) {
                this.passErrors += 'Las contraseñas no coinciden.\n';
                return false;
            }
            return true;
        }
        return true;
    }

    public togglePassword(): void {
        if (this.getType() === FormType.CREATION) {
            return;
        }
        if (!this.model.password) {
            this.usuariosApi.usuariosGET.toPromise({ id: this.model.id, p3: StorageManager.getUser().id } as never).then(result => {
                if (result) {
                    this.model.password_old = result[0]?.password === null ? 'Sin Contraseña' : result[0]?.password ?? '';
                }
                return;
            }
            ).catch(e => {
                console.log('catch en getPosition: ' + e);
            }
            );
        }
        this.isShowingPassword = !this.isShowingPassword;
    }

    fillCountry(): Promise<boolean> {
        return new Promise((resolve) => {
            for (let index = 0; index < paises.length; index++) {
                const pais = { label: paises[index]?.label, value: paises[index]?.value };

                this.lista_paises.filtered.push({
                    label: pais.label ?? '',
                    value: pais.value ?? null,
                });
                if ((index + 1) >= paises.length) {
                    resolve(true);
                }
            }
        });
    }

    private resolveFormGetRequest(): RequestHandler<UserModel[]> {
        const updateEndpoint = this.usuariosApi.usuariosPUT.getRequestData().endpoint;

        // Generic user forms need the generic endpoint so edition can hydrate persisted credentials too.
        if (updateEndpoint === 'generic/usuarios' || this.appName === 'manezylozano') {
            return this.usuariosApi.usuariosGenericGET;
        }

        return this.usuariosApi.usuariosGET;
    }

    private createReactiveForm(): void {
        const formType = Number(this.getType()) as FormType;
        const definition = new UsuariosFormDefinition(formType, this.model, this.createFormDefinitionContext());

        this.model = definition.model as UserModel;
        this.reactiveForm = {
            formType,
            model: this.model,
            formFields: definition.formFields,
        };
    }

    private createFormDefinitionContext(): UsuariosFormDefinitionContext {
        return {
            appName: this.appName,
            applicationType: this.applicationType,
            canRegister: this.canRegister,
            clienteName: this.clienteName,
            showPerfilUnidades: this.showPerfilUnidades,
            countryList: this.lista_paises,
            recordId: this.reactiveFormRecordId,
            getExistingUsers: () => this.getExistingUsersForValidation(),
        };
    }

    private prefetchExistingUsersForCreation(): void {
        if (this.getType() !== FormType.CREATION || this.formGetRequest.value) {
            return;
        }

        this.formGetRequest.safePerform();
    }

    private getExistingUsersForValidation(): readonly Partial<UserModel>[] {
        return this.usuariosApi.usuariosGET.value ?? this.formGetRequest.value ?? [];
    }
}
